import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useAdAccount } from "../lib/adAccount";
import {
  Trophy, Skull, Pulse, Sparkle, ChatTeardropDots, X,
  UsersThree, GenderIntersex, DeviceMobile, MapPin, Heart, Info, Target, Warning
} from "@phosphor-icons/react";
import DateRangePicker from "../components/DateRangePicker";
import RefreshButton from "../components/RefreshButton";
import LiveSyncButton from "../components/LiveSyncButton";

const fmtMoney = (n) => "RM" + (n || 0).toLocaleString("en-MY", { maximumFractionDigits: 2 });
const fmtBudget = (cents) => "RM" + (cents / 100).toLocaleString("en-MY");
const fmtNum = (n) => (n || 0).toLocaleString("en-MY");

const ADSET_VERDICTS = {
  winner: { icon: Trophy, label: "WINNER", color: "text-emerald-400", border: "border-emerald-500/40", bg: "bg-emerald-500/5" },
  fatigued: { icon: Warning, label: "FATIGUED", color: "text-amber-400", border: "border-amber-500/40", bg: "bg-amber-500/5" },
  loser: { icon: Skull, label: "LOSER", color: "text-red-400", border: "border-red-500/40", bg: "bg-red-500/5" },
  normal: { icon: Pulse, label: "AVERAGE", color: "text-zinc-400", border: "border-zinc-800", bg: "bg-zinc-900/30" },
};

const BREAKDOWNS = [
  { key: "age", label: "Age", icon: UsersThree, native: true },
  { key: "gender", label: "Gender", icon: GenderIntersex, native: true },
  { key: "placement", label: "Placement", icon: DeviceMobile, native: true },
  { key: "region", label: "Region", icon: MapPin, native: true },
  { key: "interest", label: "Interest", icon: Heart, native: false },
];

function TargetingTags({ t }) {
  const ageRange = t.age_min && t.age_max ? `${t.age_min}-${t.age_max}` : null;
  const genders = t.genders || [];
  const interests = t.interests || [];
  const tags = [];
  if (ageRange) tags.push({ label: ageRange, color: "border-zinc-700 text-zinc-300" });
  if (genders.length === 1) tags.push({ label: genders[0], color: genders[0] === "female" ? "border-pink-500/40 text-pink-400" : "border-blue-500/40 text-blue-400" });
  if (genders.length === 2) tags.push({ label: "All genders", color: "border-zinc-700 text-zinc-400" });
  interests.slice(0, 3).forEach(i => tags.push({ label: i, color: "border-orange-500/30 text-orange-400" }));
  if (interests.length > 3) tags.push({ label: `+${interests.length - 3}`, color: "border-zinc-700 text-zinc-500" });
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((t, i) => (
        <span key={i} className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${t.color}`}>
          {t.label}
        </span>
      ))}
    </div>
  );
}

function AdSetCard({ adset, onAskAI }) {
  const v = ADSET_VERDICTS[adset.verdict];
  const Icon = v.icon;
  const m = adset.metrics;
  return (
    <div data-testid={`adset-card-${adset.id}`} className={`bg-[#0a0a0a] border ${v.border} ${v.bg} p-4 hover:border-orange-500/60 transition-colors`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon size={11} weight="fill" className={v.color} />
          <span className={`text-[10px] font-mono uppercase tracking-wider ${v.color}`}>{v.label}</span>
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 ${
          adset.status === "ACTIVE" ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-800 text-zinc-500"
        }`}>{adset.status}</span>
      </div>

      <div className="font-medium text-sm text-zinc-100 mb-2 line-clamp-1">{adset.name}</div>

      <div className="mb-3">
        <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1">
          <Target size={9} /> Targeting
        </div>
        <TargetingTags t={adset.targeting || {}} />
      </div>

      <div className="grid grid-cols-3 gap-2 py-2.5 border-y border-zinc-800/40">
        <div>
          <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">Spend</div>
          <div className="text-[13px] font-display font-bold text-zinc-100 mt-0.5">{fmtMoney(m.spend)}</div>
        </div>
        <div>
          <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">Subs</div>
          <div className="text-[13px] font-display font-bold text-emerald-400 mt-0.5">{fmtNum(m.subscribes)}</div>
        </div>
        <div>
          <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">CPS</div>
          <div className={`text-[13px] font-display font-bold mt-0.5 ${m.cost_per_subscribe > 0 && m.cost_per_subscribe <= 12 ? "text-emerald-400" : m.cost_per_subscribe <= 20 ? "text-zinc-100" : "text-red-400"}`}>
            {m.cost_per_subscribe ? fmtMoney(m.cost_per_subscribe) : "—"}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2.5 text-[10px] font-mono">
        <span className="text-zinc-500">Budget {fmtBudget(adset.daily_budget)}/day</span>
        <button
          data-testid={`adset-ask-ai-${adset.id}`}
          onClick={() => onAskAI(adset)}
          className="flex items-center gap-1 uppercase tracking-wider text-orange-400 hover:text-orange-300 border border-orange-500/30 hover:border-orange-500 px-2 py-1 transition-colors"
        >
          <Sparkle size={10} weight="fill" /> Ask AI
        </button>
      </div>
    </div>
  );
}

function BreakdownPanel({ data, dimension, loading, onAskAI }) {
  if (loading) return <div className="text-zinc-500 text-sm py-8 text-center">Loading {dimension}...</div>;
  if (!data || data.items.length === 0) return <div className="text-zinc-500 text-sm py-8 text-center">No data for {dimension}</div>;

  const items = data.items;
  const maxSubs = Math.max(...items.map(i => i.subscribes), 1);
  const bestCPS = Math.min(...items.filter(i => i.cost_per_subscribe > 0).map(i => i.cost_per_subscribe));
  const worstCPS = Math.max(...items.map(i => i.cost_per_subscribe));

  return (
    <div>
      {!data.is_native && data.caveat && (
        <div className="bg-amber-500/10 border border-amber-500/30 px-3 py-2 mb-3 text-xs text-amber-300 flex items-start gap-2" data-testid="breakdown-caveat">
          <Info size={13} weight="fill" className="shrink-0 mt-0.5" />
          <div>{data.caveat}</div>
        </div>
      )}
      <div className="bg-zinc-900/40 border border-zinc-800/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-500 bg-black/30">
              <th className="text-left px-4 py-2.5 font-medium">Segment</th>
              <th className="text-left px-3 py-2.5 font-medium w-1/3">Subscribe Share</th>
              <th className="text-right px-3 py-2.5 font-medium">Spend</th>
              <th className="text-right px-3 py-2.5 font-medium">CTR</th>
              <th className="text-right px-3 py-2.5 font-medium">Subs</th>
              <th className="text-right px-4 py-2.5 font-medium">Cost / Sub</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, idx) => {
              const isBest = row.cost_per_subscribe === bestCPS && row.subscribes > 0;
              const isWorst = row.cost_per_subscribe === worstCPS && idx > 0;
              const barPct = (row.subscribes / maxSubs) * 100;
              return (
                <tr key={row.value} className="border-t border-zinc-800/40 hover:bg-zinc-900/40" data-testid={`breakdown-row-${row.value}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {isBest && <Trophy size={11} weight="fill" className="text-emerald-400" />}
                      {isWorst && <Skull size={11} weight="fill" className="text-red-400" />}
                      <span className="font-medium capitalize">{row.value}</span>
                      {row.adset_count != null && (
                        <span className="text-[10px] font-mono text-zinc-500">({row.adset_count} adset{row.adset_count > 1 ? "s" : ""})</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-zinc-800 relative overflow-hidden">
                        <div
                          className={`h-full ${isBest ? "bg-emerald-400" : isWorst ? "bg-red-400" : "bg-orange-400"}`}
                          style={{ width: `${barPct}%` }}
                        />
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500 w-10 text-right">{row.share_pct != null ? row.share_pct + "%" : ""}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono">{fmtMoney(row.spend)}</td>
                  <td className="px-3 py-3 text-right font-mono">{row.ctr}%</td>
                  <td className="px-3 py-3 text-right font-mono font-semibold text-emerald-400">{fmtNum(row.subscribes)}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${row.cost_per_subscribe > 0 && row.cost_per_subscribe <= 12 ? "text-emerald-400" : row.cost_per_subscribe <= 20 ? "text-zinc-200" : "text-red-400"}`}>
                    {row.cost_per_subscribe ? fmtMoney(row.cost_per_subscribe) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex justify-end">
        <button
          data-testid="breakdown-ask-ai"
          onClick={() => onAskAI(dimension, items)}
          className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-orange-400 hover:text-orange-300 border border-orange-500/30 hover:border-orange-500 px-3 py-1.5 transition-colors"
        >
          <Sparkle size={11} weight="fill" /> Ask AI to recommend action
        </button>
      </div>
    </div>
  );
}

export default function Audience() {
  const navigate = useNavigate();
  const { selected } = useAdAccount();
  const [tab, setTab] = useState("adsets"); // 'adsets' | 'breakdowns'
  const [breakdown, setBreakdown] = useState("age");

  const [adsets, setAdsets] = useState([]);
  const [adsetsLoading, setAdsetsLoading] = useState(true);
  const [breakdownData, setBreakdownData] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const [range, setRange] = useState({ mode: "preset", days: 7 });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filter, setFilter] = useState("all");

  const buildParams = () => {
    const p = new URLSearchParams({ ad_account_id: selected });
    if (range.mode === "preset") p.set("days", range.days);
    else { p.set("date_start", range.date_start); p.set("date_stop", range.date_stop); }
    return p;
  };

  const loadAdsets = async () => {
    if (!selected) return;
    setAdsetsLoading(true);
    try {
      const r = await api.get(`/audience/adsets?${buildParams()}`);
      setAdsets(r.data || []);
      setLastUpdated(Date.now());
    } finally {
      setAdsetsLoading(false);
    }
  };

  const loadBreakdown = async () => {
    if (!selected) return;
    setBreakdownLoading(true);
    try {
      const p = buildParams();
      p.set("breakdown", breakdown);
      const r = await api.get(`/audience/breakdowns?${p}`);
      setBreakdownData(r.data);
      setLastUpdated(Date.now());
    } finally {
      setBreakdownLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "adsets") loadAdsets();
    else loadBreakdown();
  }, [selected, range, tab, breakdown]); // eslint-disable-line

  const filteredAdsets = filter === "all" ? adsets : adsets.filter(a => a.verdict === filter);
  const counts = adsets.reduce((acc, a) => ({ ...acc, [a.verdict]: (acc[a.verdict] || 0) + 1, all: (acc.all || 0) + 1 }), {});

  const askAdsetAI = (adset) => {
    const t = adset.targeting || {};
    const tag = `Age ${t.age_min}-${t.age_max}, ${(t.genders || []).join("+") || "all"}, interests: ${(t.interests || []).join(", ")}`;
    const prompt = `Analyze ad set "${adset.name}" (verdict: ${adset.verdict}, spend RM${adset.metrics.spend}, subscribes ${adset.metrics.subscribes}, cost/sub RM${adset.metrics.cost_per_subscribe}). Targeting: ${tag}. Apa patut buat?`;
    sessionStorage.setItem("prefilledPrompt", prompt);
    navigate("/");
  };

  const askBreakdownAI = (dim, items) => {
    const top = items.slice(0, 3).map(i => `${i.value} (RM${i.cost_per_subscribe}/sub, ${i.subscribes} subs)`).join("; ");
    const bot = items.slice(-2).map(i => `${i.value} (RM${i.cost_per_subscribe}/sub)`).join("; ");
    const prompt = `Berdasarkan ${dim} breakdown — top performers: ${top}. Bottom: ${bot}. Apa actionable recommendation untuk improve cost per subscribe?`;
    sessionStorage.setItem("prefilledPrompt", prompt);
    navigate("/");
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-zinc-900 px-6 flex items-center backdrop-blur-xl bg-zinc-950/70 sticky top-0 z-10">
        <div>
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Audience</div>
          <div className="text-sm font-medium">Targeting performance · Subscribe-focused</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <LiveSyncButton onSynced={tab === "adsets" ? loadAdsets : loadBreakdown} />
          <RefreshButton lastUpdated={lastUpdated} loading={adsetsLoading || breakdownLoading} onRefresh={tab === "adsets" ? loadAdsets : loadBreakdown} />
          <DateRangePicker value={range} onChange={setRange} />
        </div>
      </div>

      <div className="p-6">
        {/* Tab toggle */}
        <div className="flex items-center gap-1 border border-zinc-800 p-0.5 w-fit mb-5">
          <button
            data-testid="tab-adsets"
            onClick={() => setTab("adsets")}
            className={`px-4 py-1.5 text-xs font-mono tracking-wider uppercase transition-colors ${tab === "adsets" ? "bg-orange-500 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-zinc-100"}`}
          >
            Ad Sets
          </button>
          <button
            data-testid="tab-breakdowns"
            onClick={() => setTab("breakdowns")}
            className={`px-4 py-1.5 text-xs font-mono tracking-wider uppercase transition-colors ${tab === "breakdowns" ? "bg-orange-500 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-zinc-100"}`}
          >
            Breakdowns
          </button>
        </div>

        {tab === "adsets" && (
          <>
            <div className="flex items-center gap-1.5 mb-5 flex-wrap">
              {[
                { key: "all", label: "All", icon: Pulse },
                { key: "winner", label: "Winners", icon: Trophy },
                { key: "normal", label: "Average", icon: Pulse },
                { key: "fatigued", label: "Fatigued", icon: Warning },
                { key: "loser", label: "Losers", icon: Skull },
              ].map(f => {
                const FIcon = f.icon;
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    data-testid={`filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors border ${
                      active ? "bg-orange-500 text-zinc-950 border-orange-500 font-semibold" : "border-zinc-800 hover:border-zinc-700 text-zinc-300"
                    }`}
                  >
                    <FIcon size={12} weight={active ? "fill" : "regular"} />
                    {f.label}
                    <span className={`text-[10px] font-mono ${active ? "text-zinc-900" : "text-zinc-500"}`}>{counts[f.key] || 0}</span>
                  </button>
                );
              })}
            </div>

            {adsetsLoading && <div className="text-zinc-500 text-sm">Loading ad sets...</div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAdsets.map(adset => (
                <AdSetCard key={adset.id} adset={adset} onAskAI={askAdsetAI} />
              ))}
            </div>
            {!adsetsLoading && filteredAdsets.length === 0 && (
              <div className="text-center py-16 text-zinc-500 text-sm">No ad sets in this category</div>
            )}
          </>
        )}

        {tab === "breakdowns" && (
          <>
            <div className="flex items-center gap-1.5 mb-5 flex-wrap">
              {BREAKDOWNS.map(b => {
                const BIcon = b.icon;
                const active = breakdown === b.key;
                return (
                  <button
                    key={b.key}
                    data-testid={`breakdown-${b.key}`}
                    onClick={() => setBreakdown(b.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors border ${
                      active ? "bg-orange-500 text-zinc-950 border-orange-500 font-semibold" : "border-zinc-800 hover:border-zinc-700 text-zinc-300"
                    }`}
                  >
                    <BIcon size={12} weight={active ? "fill" : "regular"} />
                    {b.label}
                    {!b.native && <span className={`text-[9px] uppercase font-mono ${active ? "text-zinc-900/70" : "text-amber-400"}`}>~</span>}
                  </button>
                );
              })}
            </div>
            <BreakdownPanel data={breakdownData} dimension={breakdown} loading={breakdownLoading} onAskAI={askBreakdownAI} />
          </>
        )}
      </div>
    </div>
  );
}
